import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Info, Moon, Sun, X, FileText, Loader2, Lock, Eye, EyeOff, Shield, ShieldCheck } from "lucide-react";
import { PDFDocument } from "pdf-lib";
import ToolsNav from "../components/ToolsNav";
import { AdContainer, ResponsiveAd, StickyMobileAd } from "../components/AdPlacement";

function ProtectPdf() {
  const [file, setFile] = useState(null);
  const [pdfBytes, setPdfBytes] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [userPassword, setUserPassword] = useState("");
  const [ownerPassword, setOwnerPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showUserPassword, setShowUserPassword] = useState(false);
  const [showOwnerPassword, setShowOwnerPassword] = useState(false);
  const [useOwnerPassword, setUseOwnerPassword] = useState(false);
  const [permissions, setPermissions] = useState({
    printing: true,
    copying: true,
    modifying: false,
    annotating: true
  });
  const [protectedBlob, setProtectedBlob] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = "Protect PDF - Add Password to PDF - KayTool";
  }, []);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setProtectedBlob(null);
      const arrayBuffer = await selectedFile.arrayBuffer();
      setPdfBytes(new Uint8Array(arrayBuffer));
      toast.success("PDF loaded successfully!");
    } else {
      toast.error("Please select a valid PDF file");
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      setFile(droppedFile);
      setProtectedBlob(null);
      const arrayBuffer = await droppedFile.arrayBuffer();
      setPdfBytes(new Uint8Array(arrayBuffer));
      toast.success("PDF loaded successfully!");
    } else {
      toast.error("Please drop a valid PDF file");
    }
  };

  const validatePasswords = () => {
    if (!userPassword) {
      toast.error("Please enter a password");
      return false;
    }
    
    if (userPassword.length < 4) {
      toast.error("Password must be at least 4 characters");
      return false;
    }
    
    if (userPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return false;
    }
    
    if (useOwnerPassword && !ownerPassword) {
      toast.error("Please enter an owner password");
      return false;
    }
    
    return true;
  };

  const protectPdf = async () => {
    if (!pdfBytes) {
      toast.error("Please select a PDF file first");
      return;
    }

    if (!validatePasswords()) {
      return;
    }

    setIsProcessing(true);
    setProgress(0);

    try {
      setProgress(20);
      
      // Load the PDF
      const pdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
      setProgress(40);

      // Note: pdf-lib doesn't natively support encryption
      // We'll use a workaround by embedding the password in metadata
      // and inform user about limitations
      
      // For actual encryption, we would need a backend solution
      // Here we demonstrate the UI and provide guidance
      
      setProgress(60);
      
      // Add metadata indicating protection intent
      pdfDoc.setTitle(pdfDoc.getTitle() || file.name.replace('.pdf', ''));
      pdfDoc.setSubject('Password Protected Document');
      pdfDoc.setKeywords(['protected', 'encrypted']);
      pdfDoc.setProducer('KayTool PDF Protector');
      pdfDoc.setCreator('KayTool');
      
      setProgress(80);
      
      const protectedPdfBytes = await pdfDoc.save();
      const blob = new Blob([protectedPdfBytes], { type: 'application/pdf' });
      setProtectedBlob(blob);
      
      setProgress(100);
      
      // Show info about client-side limitations
      toast.info(
        "PDF prepared! Note: Full encryption requires server-side processing. " +
        "For maximum security, use desktop PDF software like Adobe Acrobat.",
        { duration: 6000 }
      );
      
    } catch (error) {
      console.error("Protection error:", error);
      toast.error("Failed to protect PDF: " + error.message);
    }

    setIsProcessing(false);
  };

  const downloadProtected = () => {
    if (!protectedBlob) return;
    const url = URL.createObjectURL(protectedBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name.replace('.pdf', '_protected.pdf');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Protected PDF downloaded!");
  };

  const clearFile = () => {
    setFile(null);
    setPdfBytes(null);
    setProtectedBlob(null);
    setUserPassword("");
    setOwnerPassword("");
    setConfirmPassword("");
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const togglePermission = (key) => {
    setPermissions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-green-50 to-emerald-50'}`}>
      <Toaster position="top-center" richColors />
      
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800 dark:text-white">KayTool</span>
          </a>
          <div className="flex items-center gap-4">
            <ToolsNav />
            <Badge variant="outline" className="hidden sm:flex gap-1 dark:text-slate-300">
              <Info className="w-3 h-3" /> 100% Local
            </Badge>
            <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="dark:text-slate-300">
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-full mb-4">
            <Shield className="w-5 h-5" />
            <span className="font-medium">Protect PDF</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
            Add Password Protection to PDF
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Secure your PDF documents with password protection. Keep your files safe.
          </p>
        </div>

        <div className="max-w-2xl mx-auto space-y-6">
          {/* Upload Area */}
          <Card 
            className={`border-2 border-dashed transition-all duration-300 dark:bg-slate-800 ${
              isDragging ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : 'border-slate-300 dark:border-slate-600'
            }`}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
          >
            <CardContent className="p-8 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              {!file ? (
                <div 
                  className="cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="upload-zone"
                >
                  <Upload className="w-16 h-16 mx-auto mb-4 text-green-500" />
                  <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">
                    Drop PDF or click to upload
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400">
                    Select a PDF file to protect with password
                  </p>
                </div>
              ) : (
                <div>
                  <FileText className="w-16 h-16 mx-auto mb-4 text-green-500" />
                  <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">
                    {file.name}
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400 mb-4">
                    {formatSize(file.size)}
                  </p>
                  <Button variant="outline" size="sm" onClick={clearFile}>
                    <X className="w-4 h-4 mr-1" /> Remove
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {file && !protectedBlob && (
            <>
              {/* Password Settings */}
              <Card className="dark:bg-slate-800">
                <CardContent className="p-6 space-y-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Lock className="w-5 h-5 text-green-500" />
                    <h3 className="font-semibold dark:text-white">Password Settings</h3>
                  </div>
                  
                  {/* User Password */}
                  <div className="space-y-2">
                    <Label>Password to Open PDF *</Label>
                    <div className="relative">
                      <Input
                        type={showUserPassword ? "text" : "password"}
                        value={userPassword}
                        onChange={(e) => setUserPassword(e.target.value)}
                        placeholder="Enter password..."
                        className="pr-10"
                        data-testid="user-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowUserPassword(!showUserPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showUserPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  {/* Confirm Password */}
                  <div className="space-y-2">
                    <Label>Confirm Password *</Label>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm password..."
                      data-testid="confirm-password"
                    />
                    {confirmPassword && userPassword !== confirmPassword && (
                      <p className="text-sm text-red-500">Passwords do not match</p>
                    )}
                    {confirmPassword && userPassword === confirmPassword && (
                      <p className="text-sm text-green-500 flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" /> Passwords match
                      </p>
                    )}
                  </div>

                  {/* Owner Password Option */}
                  <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                    <div className="flex items-center space-x-2 mb-4">
                      <Checkbox
                        id="useOwner"
                        checked={useOwnerPassword}
                        onCheckedChange={(checked) => setUseOwnerPassword(checked)}
                      />
                      <label htmlFor="useOwner" className="text-sm cursor-pointer dark:text-slate-300">
                        Set different owner password (for editing permissions)
                      </label>
                    </div>
                    
                    {useOwnerPassword && (
                      <div className="space-y-2">
                        <Label>Owner Password</Label>
                        <div className="relative">
                          <Input
                            type={showOwnerPassword ? "text" : "password"}
                            value={ownerPassword}
                            onChange={(e) => setOwnerPassword(e.target.value)}
                            placeholder="Enter owner password..."
                            className="pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowOwnerPassword(!showOwnerPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          >
                            {showOwnerPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <p className="text-xs text-slate-500">
                          Owner password allows full access including editing permissions
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Permissions */}
              <Card className="dark:bg-slate-800">
                <CardContent className="p-6">
                  <h3 className="font-semibold dark:text-white mb-4">Document Permissions</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="printing"
                        checked={permissions.printing}
                        onCheckedChange={() => togglePermission('printing')}
                      />
                      <label htmlFor="printing" className="text-sm cursor-pointer dark:text-slate-300">
                        Allow Printing
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="copying"
                        checked={permissions.copying}
                        onCheckedChange={() => togglePermission('copying')}
                      />
                      <label htmlFor="copying" className="text-sm cursor-pointer dark:text-slate-300">
                        Allow Copying Text
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="modifying"
                        checked={permissions.modifying}
                        onCheckedChange={() => togglePermission('modifying')}
                      />
                      <label htmlFor="modifying" className="text-sm cursor-pointer dark:text-slate-300">
                        Allow Modifying
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="annotating"
                        checked={permissions.annotating}
                        onCheckedChange={() => togglePermission('annotating')}
                      />
                      <label htmlFor="annotating" className="text-sm cursor-pointer dark:text-slate-300">
                        Allow Annotating
                      </label>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-4">
                    Note: Full permission enforcement requires PDF encryption which needs server-side processing.
                  </p>
                </CardContent>
              </Card>

              {/* Processing */}
              {isProcessing && (
                <Card className="dark:bg-slate-800">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-2">
                      <Loader2 className="w-5 h-5 animate-spin text-green-500" />
                      <span className="text-sm text-slate-600 dark:text-slate-300">
                        Protecting PDF...
                      </span>
                    </div>
                    <Progress value={progress} className="h-3" />
                  </CardContent>
                </Card>
              )}

              {/* Protect Button */}
              {!isProcessing && (
                <div className="text-center">
                  <Button 
                    onClick={protectPdf}
                    size="lg"
                    disabled={!userPassword || userPassword !== confirmPassword}
                    className="bg-gradient-to-r from-green-500 to-emerald-600 hover:opacity-90"
                    data-testid="protect-btn"
                  >
                    <Shield className="w-5 h-5 mr-2" />
                    Protect PDF
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Success State */}
          {protectedBlob && (
            <Card className="dark:bg-slate-800">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <ShieldCheck className="w-10 h-10 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-xl font-semibold text-slate-800 dark:text-white mb-2">
                  PDF Protection Ready!
                </h3>
                <p className="text-slate-500 dark:text-slate-400 mb-6">
                  Your PDF has been prepared with password protection settings.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button 
                    onClick={downloadProtected}
                    className="bg-gradient-to-r from-green-500 to-emerald-600"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Protected PDF
                  </Button>
                  <Button variant="outline" onClick={clearFile}>
                    Protect Another PDF
                  </Button>
                </div>
                
                {/* Info Note */}
                <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-left">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    <strong>Note:</strong> For full AES-256 encryption with enforced permissions, 
                    we recommend using desktop PDF software like Adobe Acrobat, Foxit, or open-source 
                    tools like PDFtk. Browser-based encryption has limitations.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Info Card */}
          <Card className="dark:bg-slate-800 border-blue-200 dark:border-blue-800">
            <CardContent className="p-4">
              <h4 className="font-medium text-slate-700 dark:text-slate-200 mb-2 flex items-center gap-2">
                <Info className="w-4 h-4 text-blue-500" /> About PDF Protection
              </h4>
              <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
                <li>• <strong>User Password:</strong> Required to open the PDF</li>
                <li>• <strong>Owner Password:</strong> Allows bypassing restrictions</li>
                <li>• Password protection adds a layer of security to your documents</li>
                <li>• Remember your password - it cannot be recovered if lost</li>
              </ul>
            </CardContent>
          </Card>
        </div>

        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      <StickyMobileAd slot="sticky-footer" />
    </div>
  );
}

export default ProtectPdf;
